import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Phone, X } from 'lucide-react';

const TRIGGER_DELAY_MS = 75000; // 75 seconds (middle of 60-90s range)
const MAX_TRIGGER_SHOWS = 3;
const STORAGE_KEY = 'mif_quick_setup_trigger_count';

/**
 * Smart trigger that appears when user pauses on Add Investments for 60-90 seconds.
 * Shows up to 3 times across sessions.
 */
export default function QuickSetupTrigger({ onRequestCall }) {
  const [visible, setVisible] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    const count = parseInt(localStorage.getItem(STORAGE_KEY) || '0', 10);
    if (count >= MAX_TRIGGER_SHOWS) return;

    // Start inactivity timer
    timerRef.current = setTimeout(() => {
      setVisible(true);
      localStorage.setItem(STORAGE_KEY, String(count + 1));
    }, TRIGGER_DELAY_MS);

    // Reset timer on user interaction
    const resetTimer = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        const c = parseInt(localStorage.getItem(STORAGE_KEY) || '0', 10);
        if (c < MAX_TRIGGER_SHOWS) {
          setVisible(true);
          localStorage.setItem(STORAGE_KEY, String(c + 1));
        }
      }, TRIGGER_DELAY_MS);
    };

    const events = ['keydown', 'click', 'scroll'];
    events.forEach(e => document.addEventListener(e, resetTimer, { passive: true }));

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      events.forEach(e => document.removeEventListener(e, resetTimer));
    };
  }, []);

  if (!visible) return null;

  return (
    <div className="fixed bottom-6 right-6 z-40 animate-in slide-in-from-bottom-4 fade-in duration-500"
      data-testid="quick-setup-trigger">
      <div className="bg-white border border-blue-200 shadow-xl rounded-2xl p-4 max-w-xs">
        <button onClick={() => setVisible(false)}
          className="absolute top-2 right-2 text-slate-400 hover:text-slate-600">
          <X className="w-4 h-4" />
        </button>
        <p className="text-sm font-medium text-slate-800 mb-1">Need help getting started?</p>
        <p className="text-xs text-slate-500 mb-3">
          Book a quick 10-15 min call with our team. We'll walk you through it.
        </p>
        <Button size="sm" className="w-full bg-blue-600 hover:bg-blue-700"
          onClick={() => { setVisible(false); onRequestCall?.(); }}
          data-testid="quick-setup-trigger-cta">
          <Phone className="w-3.5 h-3.5 mr-1.5" />
          Quick Virtual Setup Session
        </Button>
      </div>
    </div>
  );
}
